/**
 * File: apps/qbench-cp/reactapp/src/shared/components/common/Input.jsx
 *
 * Purpose: A styled text input with optional clear and leading/trailing icon buttons.
 *
 * Scope: Shared common UI components used across customer portal forms and filters.
 *
 * Tags: customer-portal
 *
 * Overview: Wraps a native input element in a styled container that applies box-shadow,
 *     padding, and sizing driven by `height`/`width` props. Supports an optional clear icon
 *     shown when a value is present, and an optional icon or icon component placed by
 *     `iconPosition` — trailing by default, or `leading` to sit before the input, as a search
 *     field conventionally reads — both wired to click handlers. An optional `trailingAction` node renders
 *     last, divided off at the field's trailing edge, for a control that acts on the search
 *     rather than on the text (the CPv2 order search puts its samples-only toggle there).
 *     Forwards `onChange`, `onKeyDown` (via `onEnter`), placeholder, and any other native
 *     input props through to the input element.
 *
 * Exports: Input component (named export).
 */

import React from 'react'
import styled from 'styled-components';

const StyledInput = styled.div `
    display: flex;
    border-radius: 4px;
    height: ${({height}) => height ? `${height}px` : '100%'};
    box-shadow: 0px 0px 1px 1px rgba(176, 176, 176, 0.7) inset;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.2px;
    font-weight: 400;
    align-items: center;
    border-radius: 4px;
    padding: ${props => props.clearIcon ? '6px 4px 6px 15px' : '6px 15px'};
    width: ${({width}) => width ? `${width}px` : '100%'};

    & > input{
        border: none;
        border-radius: 4px;
        width: 100%;
        color: ${props => props.theme.color.palette.text.dark};
        &:focus {
            outline: none;
        }
    }

    & .icon {
        &:hover {
            cursor: pointer;
            background: #dfdfdf;
            border-radius: 4px;
        }
    }

    /* Sits before the input, so it needs the gap on its trailing side instead. */
    & .icon.leading {
        display: flex;
        align-items: center;
        flex: none;
        margin-right: 8px;
    }

    /* Layout only — the action itself is sized by whoever passes it, so no caller-specific
       rules (or another library's class names) land in this shared component. */
    & .trailingAction {
        display: flex;
        align-items: center;
        flex: none;
        margin-left: 6px;
        padding-left: 6px;
        border-left: 1px solid rgba(176, 176, 176, 0.7);
    }
`
export const Input = ({
    value = '',
    onChange = () => {},
    placeholder,
    iconComponent,
    icon,
    iconPosition = 'trailing',
    clearIcon,
    style,
    onEnter = () => {},
    onIconClick = () => {},
    onClearIconClick = () => {},
    trailingAction,
    ...props
}) => {
    const isLeading = iconPosition === 'leading';
    const iconNode = (iconComponent || icon) &&
        <div className={isLeading ? 'icon leading' : 'icon'} id='inputSubmit' onClick={onIconClick}>
            {iconComponent && iconComponent}
            {!iconComponent && icon && <img src={icon} alt="search"/>}
        </div>

    return (
        <StyledInput style={style} clearIcon={clearIcon}>
            {isLeading && iconNode}
            <input
                value={value}
                onChange={onChange}
                onKeyDown={onEnter}
                placeholder={placeholder}
                {...props}
            />
            {(clearIcon && value) &&
                <div className='icon' id='inputSubmit' onClick={onClearIconClick}>
                    <img src={clearIcon} alt="search"/>
                </div>
            }
            {!isLeading && iconNode}
            {trailingAction &&
                <div className='trailingAction'>{trailingAction}</div>
            }
        </StyledInput>
    )
}
